import { hasSameBaseLanguage } from "@/lib/learning-mode";
import { createLogger } from "@/lib/logger";
import { isLinkedWordPracticeInstructions } from "@/lib/practiceSentenceUrl";
import { capCustomInstructionsInput, capNicknameInput } from "@/lib/promptPersonalization";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = createLogger("useLocalStorage");
const LOCAL_STORAGE_CHANGE_EVENT = "chickytutor-local-storage-change";

// Generic hook for localStorage with TypeScript support
function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void, boolean] {
  // Track if component has mounted to prevent hydration mismatch
  const [mounted, setMounted] = useState(false);

  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(initialValue);
  const storedValueRef = useRef<T>(initialValue);

  // Effect to load from localStorage after component mounts
  useEffect(() => {
    const applyStoredValue = (value: T) => {
      storedValueRef.current = value;
      setStoredValue(value);
    };

    if (typeof window !== "undefined") {
      try {
        const item = window.localStorage.getItem(key);
        if (item) {
          applyStoredValue(JSON.parse(item));
        }
      } catch (error) {
        logger.error(`Error reading localStorage key "${key}":`, error);
      }

      const handleStorage = (event: StorageEvent) => {
        if (event.key !== key || !event.newValue) return;

        try {
          applyStoredValue(JSON.parse(event.newValue));
        } catch (error) {
          logger.error(`Error syncing localStorage key "${key}":`, error);
        }
      };

      const handleLocalStorageChange = (event: Event) => {
        const { detail } = event as CustomEvent<{ key: string; value: T }>;
        if (detail?.key !== key) return;
        applyStoredValue(detail.value);
      };

      window.addEventListener("storage", handleStorage);
      window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleLocalStorageChange);

      setMounted(true);

      return () => {
        window.removeEventListener("storage", handleStorage);
        window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleLocalStorageChange);
      };
    }
    setMounted(true);
  }, [key]);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const valueToStore = value instanceof Function ? value(storedValueRef.current) : value;
        storedValueRef.current = valueToStore;
        // Save state
        setStoredValue(valueToStore);
        // Save to local storage
        if (typeof window !== "undefined") {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
          window.dispatchEvent(
            new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
              detail: { key, value: valueToStore },
            })
          );
        }
      } catch (error) {
        // A more advanced implementation would handle the error case
        logger.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );

  // Return initial value until mounted to prevent hydration mismatch
  return [mounted ? storedValue : initialValue, setValue, mounted];
}

// Specific hook for user preferences
export interface UserPreferences {
  selectedLanguageCode: string;
  nativeLanguageCode: string; // Added native language code
  lastGuidedInstructionLanguageCode?: string;
  translationLanguageCode?: string;
  languageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  voice: "Puck" | "Charon" | "Kore" | "Fenrir" | "Aoede" | "Leda" | "Orus" | "Zephyr";
  teachingStyle: "neutral" | "cheeky" | "chic" | "spartan";
  nickname: string;
  customInstructions: string;
  noAvatar: boolean;
}

type UserPreferencesStorage = "persistent" | "session";

type UseUserPreferencesOptions = {
  storage?: UserPreferencesStorage;
};

const DEFAULT_USER_PREFERENCES: UserPreferences = {
  selectedLanguageCode: "",
  nativeLanguageCode: "en-US",
  lastGuidedInstructionLanguageCode: "en-US",
  languageLevel: "intermediate",
  voice: "Aoede",
  teachingStyle: "cheeky",
  nickname: "",
  customInstructions: "",
  noAvatar: false,
};

export function useUserPreferences({ storage = "persistent" }: UseUserPreferencesOptions = {}) {
  const [storedPreferences, setStoredPreferences, storedPreferencesInitialized] =
    useLocalStorage<UserPreferences>("userLanguagePreferences", DEFAULT_USER_PREFERENCES);
  const [sessionPreferences, setSessionPreferences] =
    useState<UserPreferences>(DEFAULT_USER_PREFERENCES);
  const hasHydratedSessionPreferencesRef = useRef(false);
  const preferences = storage === "session" ? sessionPreferences : storedPreferences;
  const setPreferences = storage === "session" ? setSessionPreferences : setStoredPreferences;
  const isInitialized = storedPreferencesInitialized;

  useEffect(() => {
    if (
      storage !== "session" ||
      !storedPreferencesInitialized ||
      hasHydratedSessionPreferencesRef.current
    ) {
      return;
    }

    setSessionPreferences((draft) => ({
      ...DEFAULT_USER_PREFERENCES,
      ...storedPreferences,
      selectedLanguageCode: draft.selectedLanguageCode,
      nativeLanguageCode: draft.nativeLanguageCode,
      lastGuidedInstructionLanguageCode: draft.lastGuidedInstructionLanguageCode,
      languageLevel: draft.languageLevel,
    }));
    hasHydratedSessionPreferencesRef.current = true;
  }, [storage, storedPreferences, storedPreferencesInitialized]);

  // Migration/default: ensure instruction language defaults to English if missing
  useEffect(() => {
    if (!preferences.nativeLanguageCode) {
      setPreferences((prev) => ({ ...prev, nativeLanguageCode: "en-US" }));
    }
  }, [preferences.nativeLanguageCode, setPreferences]);

  // Migration/default: ensure avatar preference exists for older localStorage payloads
  useEffect(() => {
    if (typeof preferences.noAvatar !== "boolean") {
      setPreferences((prev) => ({ ...prev, noAvatar: false }));
    }
  }, [preferences.noAvatar, setPreferences]);

  // Migration/default: ensure personalization fields exist and stay capped.
  useEffect(() => {
    const nickname = capNicknameInput(preferences.nickname);
    const customInstructions = isLinkedWordPracticeInstructions(preferences.customInstructions)
      ? ""
      : capCustomInstructionsInput(preferences.customInstructions);
    if (
      preferences.nickname !== nickname ||
      preferences.customInstructions !== customInstructions
    ) {
      setPreferences((prev) => ({
        ...prev,
        nickname,
        customInstructions,
      }));
    }
  }, [preferences.nickname, preferences.customInstructions, setPreferences]);

  // Individual setters for convenience
  const setSelectedLanguageCode = (code: string) => {
    setPreferences((prev) => ({ ...prev, selectedLanguageCode: code }));
  };

  const setLanguageLevel = (
    level: "absolute_beginner" | "beginner" | "intermediate" | "advanced"
  ) => {
    setPreferences((prev) => ({ ...prev, languageLevel: level }));
  };

  const setVoice = (
    voice: "Puck" | "Charon" | "Kore" | "Fenrir" | "Aoede" | "Leda" | "Orus" | "Zephyr"
  ) => {
    setPreferences((prev) => ({ ...prev, voice: voice }));
  };

  const setTeachingStyle = (style: "neutral" | "cheeky" | "chic" | "spartan") => {
    setPreferences((prev) => ({ ...prev, teachingStyle: style }));
  };

  const setNickname = (nickname: string) => {
    setPreferences((prev) => ({ ...prev, nickname: capNicknameInput(nickname) }));
  };

  const setCustomInstructions = (customInstructions: string) => {
    setPreferences((prev) => ({
      ...prev,
      customInstructions: capCustomInstructionsInput(customInstructions),
    }));
  };

  const setNativeLanguageCode = (code: string) => {
    setPreferences((prev) => ({ ...prev, nativeLanguageCode: code }));
  };

  const setNoAvatar = (noAvatar: boolean) => {
    setPreferences((prev) => ({ ...prev, noAvatar }));
  };

  const commitLanguagePreferencesToStorage = () => {
    setStoredPreferences((stored) => ({
      ...stored,
      selectedLanguageCode: preferences.selectedLanguageCode,
      nativeLanguageCode: preferences.nativeLanguageCode,
      translationLanguageCode: preferences.translationLanguageCode,
      languageLevel: preferences.languageLevel,
      ...(!hasSameBaseLanguage(preferences.nativeLanguageCode, preferences.selectedLanguageCode)
        ? { lastGuidedInstructionLanguageCode: preferences.nativeLanguageCode }
        : {}),
    }));
  };

  return {
    preferences,
    isInitialized, // Added isInitialized
    setSelectedLanguageCode,
    setNativeLanguageCode, // Added setter for native language code
    setLanguageLevel,
    setVoice,
    setTeachingStyle,
    setNickname,
    setCustomInstructions,
    setNoAvatar,
    commitLanguagePreferencesToStorage,
    // For bulk updates if needed
    setPreferences,
  };
}

export default useLocalStorage;
